import { ViewHook } from "phoenix_live_view";

import { evenlySpread } from "../utils/sequence";

interface Dot {
  x: number;
  y: number;
  phase: number;
  lit: boolean;
}

const INTRO_MS = 2400;
const PULSE_MS = 6500;

class ReportFundField extends ViewHook {
  declare canvas: HTMLCanvasElement;
  declare dots: Dot[];
  declare width: number;
  declare height: number;
  declare startedAt: number;
  declare frame?: number;
  declare running: boolean;
  declare reducedMotion: boolean;
  declare resizeObserver: ResizeObserver;
  declare visibilityObserver: IntersectionObserver;

  mounted() {
    this.canvas = this.el as HTMLCanvasElement;
    this.dots = [];
    this.width = 0;
    this.height = 0;
    this.running = false;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.startedAt = performance.now();

    this.tick = this.tick.bind(this);

    this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
    this.resizeObserver.observe(this.canvas);

    this.visibilityObserver = new IntersectionObserver(this.onVisibility.bind(this));
    this.visibilityObserver.observe(this.canvas);
  }

  destroyed() {
    this.stop();
    this.resizeObserver.disconnect();
    this.visibilityObserver.disconnect();
  }

  private onResize() {
    const { width, height } = this.canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;

    this.width = width;
    this.height = height;
    this.canvas.width = Math.round(width * ratio);
    this.canvas.height = Math.round(height * ratio);
    this.canvas.getContext("2d")?.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.dots = this.layout(Number(this.el.dataset.count));

    this.draw(performance.now());
  }

  private layout(count: number): Dot[] {
    const columns = Math.max(1, Math.round(Math.sqrt((count * this.width) / this.height)));
    const rows = Math.ceil(count / columns);
    const gapX = this.width / columns;
    const gapY = this.height / rows;

    return Array.from({ length: count }, (_, index) => {
      const seed = evenlySpread(index);

      return {
        x: (index % columns) * gapX + gapX / 2,
        y: Math.floor(index / columns) * gapY + gapY / 2,
        phase: seed * Math.PI * 2,
        lit: seed < 0.06,
      };
    });
  }

  private onVisibility(entries: IntersectionObserverEntry[]) {
    const visible = entries.some((entry) => entry.isIntersecting);

    if (visible && !this.reducedMotion) {
      this.start();
    } else {
      this.stop();
    }
  }

  private start() {
    if (!this.running) {
      this.running = true;
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  private stop() {
    this.running = false;

    if (this.frame !== undefined) {
      cancelAnimationFrame(this.frame);
      this.frame = undefined;
    }
  }

  private tick(now: number) {
    this.draw(now);

    if (this.running) {
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  private draw(now: number) {
    const context = this.canvas.getContext("2d");

    if (!context) {
      return;
    }

    const styles = getComputedStyle(this.canvas);
    const base = styles.getPropertyValue("--report-faint").trim();
    const accent = styles.getPropertyValue("--report-accent").trim();
    const elapsed = this.reducedMotion ? INTRO_MS : now - this.startedAt;
    const originX = this.width * 0.75;
    const originY = this.height * 0.45;
    const reach = Math.hypot(this.width, this.height);
    const pulseRadius = ((elapsed % PULSE_MS) / PULSE_MS) * 1.1;

    context.clearRect(0, 0, this.width, this.height);

    for (const dot of this.dots) {
      const distance = Math.hypot(dot.x - originX, dot.y - originY) / reach;
      const intro = Math.min(1, Math.max(0, (elapsed / INTRO_MS - distance) * 3));

      if (intro > 0) {
        const wave = Math.sin(elapsed / 1600 - distance * 10 + dot.phase * 0.3) * 0.5 + 0.5;
        const twinkle = dot.lit ? Math.max(0, Math.sin(elapsed / 900 + dot.phase)) : 0;
        const pulse = this.reducedMotion
          ? 0
          : Math.max(0, 1 - Math.abs(distance - pulseRadius) * 14);
        const glow = Math.max(twinkle, pulse * 0.8);

        context.globalAlpha = intro * Math.min(1, 0.22 + wave * 0.22 + glow * 0.75);
        context.fillStyle = glow > 0.25 ? accent : base;
        context.fillRect(dot.x - 1, dot.y - 1, 2, 2);
      }
    }

    context.globalAlpha = 1;
  }
}

export default ReportFundField;
